import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    Cell,
} from 'recharts';
import { parseISO, getDay } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

interface SeasonalDataPoint {
    dayName: string;
    dayIndex: number;
    index: number;
    avgRevenue: number;
}

export const SeasonalIndexChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance } = useBoardChartData();

    const chartData = useMemo((): SeasonalDataPoint[] => {
        if (!finance?.dailyBreakdown) return [];

        const dayTotals: Record<number, { sum: number; count: number }> = {};
        for (let i = 0; i < 7; i++) {
            dayTotals[i] = { sum: 0, count: 0 };
        }

        Object.entries(finance.dailyBreakdown).forEach(([date, day]: [string, Record<string, unknown>]) => {
            const metrics = day.metrics as Record<string, number> | undefined;
            const revenue = metrics?.revenue ?? 0;
            const dayOfWeek = getDay(parseISO(date)); // 0=Sunday, 6=Saturday
            dayTotals[dayOfWeek].sum += revenue;
            dayTotals[dayOfWeek].count += 1;
        });

        // Compute per-day averages
        const dayAvgs: Record<number, number> = {};
        let totalAvg = 0;
        let totalDaysWithData = 0;
        for (let i = 0; i < 7; i++) {
            dayAvgs[i] = dayTotals[i].count > 0 ? dayTotals[i].sum / dayTotals[i].count : 0;
            if (dayTotals[i].count > 0) {
                totalAvg += dayAvgs[i];
                totalDaysWithData += 1;
            }
        }

        const overallAvg = totalDaysWithData > 0 ? totalAvg / totalDaysWithData : 0;
        if (overallAvg === 0) return [];

        // Day names in Mon-Sun order
        const dayNames = [
            t('charts.dayMon', 'Mon'),
            t('charts.dayTue', 'Tue'),
            t('charts.dayWed', 'Wed'),
            t('charts.dayThu', 'Thu'),
            t('charts.dayFri', 'Fri'),
            t('charts.daySat', 'Sat'),
            t('charts.daySun', 'Sun'),
        ];
        // Map from Mon-Sun display order to getDay indices (Mon=1, Tue=2, ..., Sun=0)
        const dayIndices = [1, 2, 3, 4, 5, 6, 0];

        return dayIndices.map((dayIdx, i) => ({
            dayName: dayNames[i],
            dayIndex: dayIdx,
            index: Math.round((dayAvgs[dayIdx] / overallAvg) * 100),
            avgRevenue: dayAvgs[dayIdx],
        }));
    }, [finance?.dailyBreakdown, t]);

    const bestDay = useMemo(() => {
        if (chartData.length === 0) return null;
        return chartData.reduce((best, d) => d.index > best.index ? d : best, chartData[0]);
    }, [chartData]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({
        isEmpty,
        summary: !isEmpty && bestDay ? (
            <span style={{ color: CHART_COLORS.revenue }}>
                {t('charts.bestDay', 'Best: {{day}} ({{index}})', { day: bestDay.dayName, index: bestDay.index })}
            </span>
        ) : undefined,
    });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="dayName"
                            {...CHART_STYLES.axis}
                        />
                        <YAxis
                            tickFormatter={(v: number) => `${v}`}
                            {...CHART_STYLES.axis}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number) => [
                                `${value}`,
                                t('charts.seasonalIndex', 'Index'),
                            ]}
                        />
                        <ReferenceLine
                            y={100}
                            stroke={CHART_COLORS.gray[400]}
                            strokeDasharray="3 3"
                            label={{
                                value: t('charts.average', 'Avg'),
                                position: 'right',
                                fill: 'var(--text-primary)',
                                fontSize: 10,
                            }}
                        />
                        <Bar
                            dataKey="index"
                            name={t('charts.seasonalIndex', 'Index')}
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        >
                            {chartData.map((entry, idx) => (
                                <Cell
                                    key={`cell-${idx}`}
                                    fill={entry.index >= 100 ? CHART_COLORS.revenue : CHART_COLORS.expense}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
